import React, {useState, useEffect, useRef} from 'react'
import GenericTable from '../../shared/generic-table'
import '../../../styles/control-center-styles.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import advanced from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {useAuthClient} from '../../../utils/use-auth-client'
import {toast} from 'react-toastify'
import {getUserRoles} from '../../../utils/get-user-roles'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

const DEFAULT_TIMEZONE = 'America/Chicago'
const formatDate = (dateStr, timezone = DEFAULT_TIMEZONE) => {
  return dayjs(dateStr).tz(timezone).format('MM/DD/YYYY \n HH:mmA z')
}

// Custom hook for debounced value
const useDebouncedValue = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

const BuyNowLinkModal = ({link, open, onClose}) => {
  if (!open) return null

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <section className="appointment-info-modal">
        <div className="appointment-modal-header">
          <h2>Buy Now Link Details</h2>
          <button onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="appointment-info-content">
          {link && (
            <div className="appointment-details-container">
              <div className="appointment-details-grid">
                <div className="appointment-detail">
                  <span className="detail-label">Link</span>
                  <a
                    href={link.link}
                    target="_blank"
                    rel="noreferrer"
                    className="detail-value"
                    style={{color: '#2d8cff', textDecoration: 'none'}}
                  >
                    View Buy Now Link
                  </a>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Created Date</span>
                  <span className="detail-value">
                    {formatDate(link.create_date)}
                  </span>
                </div>
                <div className="appointment-detail">
                  <span className="detail-label">Order Total</span>
                  <span className="detail-value">
                    ${parseFloat(link.order_total).toFixed(2)}
                  </span>
                </div>
                <div className="appointment-detail full-width">
                  <span className="detail-label">Plan</span>
                  <div className="detail-value">
                    {Array.isArray(link?.products_in_order?.plan)
                      ? link.products_in_order.plan
                          .map(plan => plan?.name)
                          .join(', ')
                      : link?.products_in_order?.plan || 'No plan specified'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

const SharedBuyNowLinks = ({
  keapPartnerInfo,
  setDashboardLoading,
  dashboardLoading,
  title,
  userData,
}) => {
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [countPerPage] = useState(5)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLink, setSelectedLink] = useState(null)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const dateRangeRef = useRef(null)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })

  // Consolidated filter state
  const [filters, setFilters] = useState({
    influencerId: '',
    linkId: '',
    startDate: '',
    endDate: '',
  })

  // Debounced filter values for API calls
  const debouncedInfluencerId = useDebouncedValue(filters.influencerId)
  const debouncedLinkId = useDebouncedValue(filters.linkId)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)

  const client = useAuthClient()

  // Check if user has InfluencerOps role - supports both Auth0 and Okta
  const userRoles = getUserRoles(userData)
  const isInfluencerOps = userRoles.includes('InfluencerOps')

  // Filter update handlers
  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}

      // If start date is greater than end date, reset end date
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }

      return updated
    })
  }

  const clearAllFilters = () => {
    setFilters({
      influencerId: '',
      linkId: '',
      startDate: '',
      endDate: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(event.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  const baseColumns = [
    {key: 'link_id', label: 'Link ID'},
    {
      key: 'create_date',
      label: 'Created',
      render: date => (
        <p className="formmated-date" style={{fontSize: '12px', margin: 0}}>
          {formatDate(date)}
        </p>
      ),
    },
    {
      key: 'products_in_order',
      label: 'Plan',
      render: products => {
        const plan = Array.isArray(products?.plan)
          ? products.plan.map(plan => plan?.name)
          : products?.plan
        return Array.isArray(plan)
          ? plan.join(', ')
          : plan || 'No plan specified'
      },
    },
    {
      key: 'order_total',
      label: 'Order Total',
      render: total => `$${parseFloat(total).toFixed(2)}`,
    },
    {
      key: 'country_code',
      label: 'Country',
      render: countryCode => countryCode || '-',
    },
  ]

  // Add Influencer Name column only for InfluencerOps users
  const influencerNameColumn = isInfluencerOps
    ? {
        key: 'influencer_name',
        label: 'Influencer',
        render: name => name || '',
      }
    : null

  const actionColumn = {
    key: 'actions',
    label: 'Actions',
    render: (_, row) => (
      <div style={{display: 'flex', gap: '8px'}}>
        <button
          className="table-button"
          onClick={e => {
            e.stopPropagation()
            setSelectedLink(row)
          }}
        >
          Details
        </button>
        <button
          className="table-button copy-link"
          onClick={e => {
            e.stopPropagation()
            navigator.clipboard.writeText(row.link)
          }}
        >
          Copy Link
        </button>
        {/* <button
          className="table-button canceled"
          onClick={() => toast.info('This feature is not available yet')}
        >
          Cancel
        </button> */}
      </div>
    ),
  }

  const columns = influencerNameColumn
    ? [...baseColumns, influencerNameColumn, actionColumn]
    : [...baseColumns, actionColumn]

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedInfluencerId,
    debouncedLinkId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  useEffect(() => {
    const fetchBuyNowLinks = async () => {
      setIsLoading(true)
      setDashboardLoading(true)
      try {
        // Build URL with query parameters
        const params = new URLSearchParams({
          page: page.toString(),
          countPerPage: countPerPage.toString(),
        })

        if (debouncedInfluencerId)
          params.append('influencerId', debouncedInfluencerId)
        if (debouncedLinkId) params.append('linkId', debouncedLinkId)
        if (debouncedStartDate) params.append('startDate', debouncedStartDate)
        if (debouncedEndDate) params.append('endDate', debouncedEndDate)

        const url = `keappartner/fetchKeapPartnerLink/${keapPartnerInfo.keapPartnerId}?${params}`
        const data = await client(url)

        if (data.success && data.data && Array.isArray(data.data.data)) {
          setRows(data.data.data)
          if (data.data.pagination) {
            setPagination({
              page: data.data.pagination.page,
              totalPages: data.data.pagination.totalPages,
              total: data.data.pagination.total,
              hasNext: data.data.pagination.hasNext,
              hasPrev: data.data.pagination.hasPrev,
            })
          }
        }
      } catch (error) {
        console.error('Error fetching buy now links:', error)
        toast.error('Error fetching buy now links')
        setRows([])
      } finally {
        setIsLoading(false)
        setDashboardLoading(false)
      }
    }

    fetchBuyNowLinks()
  }, [
    page,
    countPerPage,
    debouncedInfluencerId,
    debouncedLinkId,
    debouncedStartDate,
    debouncedEndDate,
    setDashboardLoading,
    client,
    keapPartnerInfo.keapPartnerId,
  ])

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      const startStr = start ? dayjs(start).format('YYYY-MM-DD') : ''
      const endStr = end ? dayjs(end).format('YYYY-MM-DD') : ''

      updateFilter('startDate', startStr)
      updateFilter('endDate', endStr)

      if (start && end) {
        setIsDateRangeOpen(false)
      }
    }
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null

    const toLocalDate = dateStr => {
      if (!dateStr) return null
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day)
    }

    return [toLocalDate(filters.startDate), toLocalDate(filters.endDate)]
  }

  return (
    <>
      <div style={{padding: 16}}>
        <h3
          className="control-center-item-header"
          style={{marginBottom: '8px'}}
        >
          {title}
        </h3>
        {rows.length > 0 && (
          <h4
            className="control-center-display-text"
            style={{marginBottom: '16px'}}
          >
            Displaying {page * countPerPage - countPerPage + 1}-
            {Math.min(page * countPerPage, pagination.total)} out of{' '}
            {pagination.total}
          </h4>
        )}
        <div
          style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            border: '1px solid #e9e9e9',
          }}
        >
          {isInfluencerOps && (
            <div style={{flex: '1 1 auto', minWidth: '220px'}}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  marginBottom: '6px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  color: '#666',
                  letterSpacing: '0.5px',
                }}
              >
                Influencer ID
              </label>
              <input
                type="text"
                placeholder="Enter influencer ID to filter"
                value={filters.influencerId}
                onChange={e => updateFilter('influencerId', e.target.value)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d4d4d4',
                  fontSize: '14px',
                  fontFamily: 'Open Sans, sans-serif',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#231f20',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => (e.target.style.borderColor = '#fa6000')}
                onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
              />
            </div>
          )}
          <div style={{flex: '1 1 auto', minWidth: '250px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Link ID
            </label>
            <input
              type="text"
              placeholder="Enter link ID to filter"
              value={filters.linkId}
              onChange={e => updateFilter('linkId', e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#231f20',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
          </div>
          <div style={{flex: '1 1 auto', minWidth: '260px'}} ref={dateRangeRef}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: '#666',
                letterSpacing: '0.5px',
              }}
            >
              Date Range
            </label>
            <div style={{position: 'relative'}}>
              <input
                type="text"
                readOnly
                value={dateRangeLabel}
                placeholder="Select date range"
                onClick={() => setIsDateRangeOpen(prev => !prev)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d4d4d4',
                  fontSize: '14px',
                  fontFamily: 'Open Sans, sans-serif',
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#231f20',
                  transition: 'border-color 0.2s',
                  cursor: 'pointer',
                }}
                onFocus={e => (e.target.style.borderColor = '#fa6000')}
                onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
              />
              {isDateRangeOpen && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 20,
                    marginTop: '8px',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <Calendar
                    selectRange
                    onChange={handleDateRangeChange}
                    value={getCalendarValue()}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={{flex: '0 0 auto', minWidth: '120px'}}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '6px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: 'transparent',
                letterSpacing: '0.5px',
              }}
            >
              &nbsp;
            </label>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '10px 16px',
                borderRadius: '4px',
                border: '1px solid #d4d4d4',
                fontSize: '14px',
                fontFamily: 'Open Sans, sans-serif',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                color: '#fa6000',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#fa6000'
                e.target.style.color = '#ffffff'
                e.target.style.borderColor = '#fa6000'
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = '#ffffff'
                e.target.style.color = '#fa6000'
                e.target.style.borderColor = '#d4d4d4'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      {rows.length > 0 && (
        <div style={{padding: 16}}>
          <div className="control-center-table-wrapper">
            <GenericTable
              columns={columns}
              rows={rows}
              emptyMessage="No buy now links found."
              isLoading={isLoading}
              loadingMessage="Loading buy now links..."
              onRowClick={row => setSelectedLink(row)}
              pagination={{
                page: pagination.page,
                totalPages: pagination.totalPages,
                hasPrev: pagination.hasPrev,
                hasNext: pagination.hasNext,
                onPageChange: setPage,
              }}
            />
          </div>
          <BuyNowLinkModal
            link={selectedLink}
            open={!!selectedLink}
            onClose={() => setSelectedLink(null)}
          />
        </div>
      )}

      {!(rows.length > 0) && !dashboardLoading && (
        <div style={{padding: 16}}>
          <div
            style={{
              textAlign: 'center',
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          >
            <h4
              style={{
                color: '#808080',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              No Active 1:Many Links Found
            </h4>
            <p style={{color: '#999', fontSize: '14px', marginTop: '8px'}}>
              Try adjusting your filters to see more results
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default SharedBuyNowLinks
